import React from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Section } from '../settingsPrimitives';
import CompactSelectPicker from '../ui/CompactSelectPicker';
import { usePreferencesStore } from '../../store/preferencesStore';
import { clampTerminalFontSize, normalizeTerminalTheme } from '../../utils/terminalTheme';
import { normalizeCloseOnExit } from '../../utils/terminalCloseOnExit';
import TerminalProfilesSettings from './TerminalProfilesSettings';
import TerminalDefaultCwdSetting from './TerminalDefaultCwdSetting';
import TerminalShellVisibilitySetting from './TerminalShellVisibilitySetting';
import TerminalKeyboardSetting from './TerminalKeyboardSetting';
import TerminalPaletteKeySetting from './TerminalPaletteKeySetting';
import TerminalNotifySetting from './TerminalNotifySetting';
import KnownHostsSettings from '../ssh/KnownHostsSettings';

/**
 * Terminal extension settings, shown under Settings → Extensions → Terminal.
 * The font size is a persisted app preference (`terminalFontSize`, default
 * 11) applied live to every open local and SSH terminal by the terminal
 * hooks — no reopen needed. "When a program exits" (`terminalCloseOnExit`,
 * default 'clean') decides whether a pane closes itself. Below them, the
 * agent profiles of the "+" menu (TerminalProfilesSettings).
 */
export default function TerminalExtensionSettings() {
  const { t } = useTranslation();
  const terminalFontSize = usePreferencesStore((s) => s.terminalFontSize);
  const closeOnExit = usePreferencesStore((s) => s.terminalCloseOnExit);
  const terminalTheme = usePreferencesStore((s) => s.terminalTheme);
  const update = usePreferencesStore((s) => s.updatePreference);

  return (
    <>
      <Section title={t('terminalExt.settingsAppearance', 'Appearance')}>
        <Row
          label={t('terminalExt.theme', 'Theme')}
          desc={t(
            'terminalExt.themeDesc',
            'Follow the app theme, or keep the terminal always dark or always light. Applies to every open terminal immediately.',
          )}
        >
          <CompactSelectPicker
            aria-label={t('terminalExt.theme', 'Theme')}
            value={normalizeTerminalTheme(terminalTheme)}
            onChange={(v) => update('terminalTheme', v)}
            menuMinWidth={200}
            options={[
              { value: 'follow', label: t('terminalExt.themeFollow', 'Follow app theme') },
              { value: 'dark', label: t('terminalExt.themeDark', 'Always dark') },
              { value: 'light', label: t('terminalExt.themeLight', 'Always light') },
            ]}
          />
        </Row>
        <Row
          label={t('terminalExt.fontSize', 'Font size')}
          desc={t(
            'terminalExt.fontSizeDesc',
            'Terminal text size in pixels (8–24). Applies to local and SSH tabs immediately.',
          )}
        >
          <div className="settings-input-group">
            <input
              type="number"
              min={8}
              max={24}
              value={terminalFontSize ?? 11}
              onChange={(e) => {
                const n = parseInt(e.target.value, 10);
                // Store the raw value while typing (the terminals clamp on
                // apply), normalize on blur so nothing odd persists.
                if (!Number.isNaN(n)) update('terminalFontSize', n);
              }}
              onBlur={(e) => {
                update('terminalFontSize', clampTerminalFontSize(e.target.value));
              }}
              className="settings-number-input"
              style={{ width: 64 }}
            />
          </div>
        </Row>
        <Row
          label={t('terminalExt.closeOnExit', 'When a program exits')}
          desc={t(
            'terminalExt.closeOnExitDesc',
            'Close the pane — the last pane closes its tab. A program that fails to start always stays open with its message.',
          )}
        >
          <CompactSelectPicker
            aria-label={t('terminalExt.closeOnExit', 'When a program exits')}
            value={normalizeCloseOnExit(closeOnExit)}
            onChange={(v) => update('terminalCloseOnExit', v)}
            menuMinWidth={240}
            options={[
              { value: 'clean', label: t('terminalExt.closeOnExitClean', 'On a clean exit (code 0)') },
              { value: 'always', label: t('terminalExt.closeOnExitAlways', 'Always') },
              { value: 'never', label: t('terminalExt.closeOnExitNever', 'Never — keep it open to restart') },
            ]}
          />
        </Row>
      </Section>
      <TerminalKeyboardSetting />
      <TerminalPaletteKeySetting />
      <TerminalNotifySetting />
      <TerminalDefaultCwdSetting />
      <TerminalShellVisibilitySetting />
      <TerminalProfilesSettings />
      <KnownHostsSettings />
    </>
  );
}
